import { apiSlice } from "./http";
import { Tags } from "../enum/tags";
import { Paginated } from "../@types/paginated";
import {
  ICampaign,
  ICampaignParams,
  ICampaignDetails,
  ICancelCampaignDto,
  IUnifiedCampaignParams,
  IUnifiedCampaignResponse,
  CampaignType,
} from "@/@types/campaign";

export const campaignService = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCampaigns: builder.query<Paginated<ICampaign>, ICampaignParams>({
      query: ({
        meta,
        search,
        status,
        startDateFrom,
        startDateTo,
        endDateFrom,
        endDateTo,
        stores,
        createdBy,
        orderBy,
      }) => {
        const perPage = meta.perPage ?? 20;
        const params = new URLSearchParams({
          page: meta.page.toString(),
          perPage: perPage.toString(),
        });

        if (search) params.append("search", search);
        if (status && status.length > 0) {
          status.forEach(s => params.append("status", s));
        }
        if (startDateFrom) params.append("startDateFrom", startDateFrom);
        if (startDateTo) params.append("startDateTo", startDateTo);
        if (endDateFrom) params.append("endDateFrom", endDateFrom);
        if (endDateTo) params.append("endDateTo", endDateTo);
        if (stores && stores.length > 0) {
          stores.forEach(storeId => params.append("stores", storeId.toString()));
        }
        if (createdBy) params.append("createdBy", createdBy);
        if (orderBy) params.append("orderBy", orderBy);

        return {
          url: `/campaigns?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.CAMPAIGNS],
    }),

    getCampaignById: builder.query<ICampaignDetails, string>({
      query: (id) => ({
        url: `/campaigns/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: Tags.CAMPAIGN, id }],
    }),

    cancelCampaign: builder.mutation<
      { message: string },
      { id: string; dto: ICancelCampaignDto }
    >({
      query: ({ id, dto }) => ({
        url: `/campaigns/${id}/cancel`,
        method: "PUT",
        body: dto,
      }),
      invalidatesTags: [Tags.CAMPAIGNS, Tags.CAMPAIGN],
    }),

    exportCampaignProducts: builder.mutation<Blob, string>({
      query: (id) => ({
        url: `/campaigns/${id}/products/export`,
        method: "GET",
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    createCampaign: builder.mutation<
      { message: string; data: any },
      {
        name: string;
        campaignType: string;
        startDate: string;
        endDate: string;
        storeIndicator: number;
        stores?: number[];
        targetGroupId?: number;
        discountTypeId?: number;
        voucherValue?: number;
        minimumPurchaseValue?: number;
        validityDays?: number;
        items?: string[];
        additionalData?: Record<string, any>;
      }
    >({
      query: (data) => ({
        url: `/campaigns`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [Tags.CAMPAIGNS],
    }),

    // ========================================
    // PRO013 SPECIFIC ENDPOINTS
    // ========================================

    createPro013Campaign: builder.mutation<
      {
        message: string;
        data:
          | { code: number; recordsCreated: number }
          | { queued: true; jobId: string | number; itemsCount: number; statusUrl: string };
      },
      {
        description: string;
        startDate: string;
        endDate: string;
        storeCode?: number;
        stores?: number[];
        takeQuantity: number;
        payQuantity: number;
        discountValue: number;
        discountType: number;
        allStoreFlag: number;
        paymentMethod: number;
        departments: number[];
        groups: number[];
      }
    >({
      query: (data) => ({
        url: `/campaigns/pro013`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [Tags.CAMPAIGNS],
    }),

    getPro013CampaignByCode: builder.query<any, number>({
      query: (code) => ({
        url: `/campaigns/pro013/${code}`,
        method: "GET",
      }),
      providesTags: (_result, _error, code) => [{ type: Tags.CAMPAIGN, id: `pro013-${code}` }],
    }),

    updatePro013Campaign: builder.mutation<
      { message: string; data: { code: number; recordsUpdated: number } },
      {
        code: number;
        description?: string;
        startDate?: string;
        endDate?: string;
        departments?: number[];
        groups?: number[];
      }
    >({
      query: ({ code, ...data }) => ({
        url: `/campaigns/pro013/${code}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { code }) => [
        Tags.CAMPAIGNS,
        { type: Tags.CAMPAIGN, id: `pro013-${code}` },
        { type: Tags.CAMPAIGN, id: `PRO013-${code}` },
        { type: Tags.CAMPAIGN, id: `audit-PRO013-${code}` },
      ],
    }),

    /**
     * Edita o conjunto de lojas de uma campanha PRO013 (body = conjunto COMPLETO).
     * 400 em campanha global/expirada; 409 com `conflicts` quando lojas adicionadas
     * têm produtos em conflito com outras campanhas.
     */
    updatePro013CampaignStores: builder.mutation<
      { message: string; data: { code: number; added: number[]; removed: number[]; noop?: boolean } },
      { code: number; stores: number[] }
    >({
      query: ({ code, stores }) => ({
        url: `/campaigns/pro013/${code}/stores`,
        method: "PATCH",
        body: { stores },
      }),
      invalidatesTags: (_result, _error, { code }) => [
        Tags.CAMPAIGNS,
        { type: Tags.CAMPAIGN, id: `pro013-${code}` },
        { type: Tags.CAMPAIGN, id: `PRO013-${code}` },
        { type: Tags.CAMPAIGN, id: `audit-PRO013-${code}` },
      ],
    }),

    // ========================================
    // PRO017 SPECIFIC ENDPOINTS
    // ========================================

    createPro017Campaign: builder.mutation<
      {
        message: string;
        data:
          | { code: number; listsCreated: number; itemsCreated: number }
          | { queued: true; jobId: string | number; itemsCount: number; statusUrl: string };
      },
      {
        description: string;
        startDate: string;
        endDate: string;
        discountValue: number;
        discountType: number;
        lists: Array<{ listNumber: number; items: string[] }>;
        conflictResolutions?: Array<{
          itemCode: string;
          conflictingCampaigns: Array<{ code: number; type: string; storeCode: number; listNumber?: number }>;
        }>;
      }
    >({
      query: (data) => ({
        url: `/campaigns/pro017`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [Tags.CAMPAIGNS],
    }),

    validatePro017Conflicts: builder.mutation<
      {
        conflicts: Array<{
          itemCode: string;
          itemName: string;
          conflictingCampaigns: Array<{
            code: number;
            description: string;
            type: string;
            startDate: string;
            endDate: string;
            storeCode: number;
            listNumber?: number;
          }>;
        }>;
        meta: { itemsCount: number; chunksValidated: number; conflictsCount: number; durationMs: number; identifierRequest: string };
      },
      { startDate: string; endDate: string; lists: Array<{ listNumber: number; items: string[] }> }
    >({
      query: (body) => ({
        url: `/campaigns/pro017/conflicts/validate`,
        method: "POST",
        body,
      }),
    }),

    getPro017CampaignByCode: builder.query<any, number>({
      query: (code) => ({
        url: `/campaigns/pro017/${code}`,
        method: "GET",
      }),
      providesTags: (_result, _error, code) => [{ type: Tags.CAMPAIGN, id: `pro017-${code}` }],
    }),

    updatePro017Campaign: builder.mutation<
      { message: string; data: { code: number; recordsUpdated: number } },
      {
        code: number;
        description?: string;
        startDate?: string;
        endDate?: string;
      }
    >({
      query: ({ code, ...data }) => ({
        url: `/campaigns/pro017/${code}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { code }) => [
        Tags.CAMPAIGNS,
        { type: Tags.CAMPAIGN, id: `pro017-${code}` },
        { type: Tags.CAMPAIGN, id: `PRO017-${code}` },
        { type: Tags.CAMPAIGN, id: `audit-PRO017-${code}` },
      ],
    }),

    getPro017ListItems: builder.query<
      {
        items: Array<{ code: string; isActive: boolean }>;
        summary: { activeCount: number; inactiveCount: number; totalCount: number };
      },
      { code: number; listNumber: number }
    >({
      query: ({ code, listNumber }) => ({
        url: `/campaigns/pro017/${code}/lists/${listNumber}/items`,
        method: "GET",
      }),
      providesTags: (_result, _error, { code, listNumber }) => [
        { type: Tags.CAMPAIGN, id: `pro017-list-${code}-${listNumber}` },
      ],
    }),

    addPro017ListItems: builder.mutation<
      { message: string; data: { code: number; listNumber: number; itemsAdded: number } },
      { code: number; listNumber: number; itemCodes: string[] }
    >({
      query: ({ code, listNumber, itemCodes }) => ({
        url: `/campaigns/pro017/${code}/lists/${listNumber}/items`,
        method: "POST",
        body: { itemCodes },
      }),
      invalidatesTags: (_result, _error, { code, listNumber }) => [
        { type: Tags.CAMPAIGN, id: `pro017-${code}` },
        { type: Tags.CAMPAIGN, id: `PRO017-${code}` },
        { type: Tags.CAMPAIGN, id: `pro017-list-${code}-${listNumber}` },
        { type: Tags.CAMPAIGN, id: `audit-PRO017-${code}` },
      ],
    }),

    // Toggle ativo/inativo consolidado. POST + path fixo + tudo no body —
    // PATCH nas rotas antigas (/activate, /deactivate) dava 502 no gateway
    // SSO de HML. Backend descomissionou aqueles endpoints; chamada antiga
    // resulta em 404. Path fixo + POST contorna gateway/WAF restritivo.
    togglePro017ListItem: builder.mutation<
      { message: string },
      { code: number; listNumber: number; itemCode: string; active: boolean }
    >({
      query: (body) => ({
        url: `/campaigns/pro017/items/toggle-status`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { code, listNumber }) => [
        { type: Tags.CAMPAIGN, id: `pro017-${code}` },
        { type: Tags.CAMPAIGN, id: `PRO017-${code}` },
        { type: Tags.CAMPAIGN, id: `pro017-list-${code}-${listNumber}` },
        { type: Tags.CAMPAIGN, id: `audit-PRO017-${code}` },
      ],
    }),

    // ========================================
    // UNIFIED CAMPAIGN ENDPOINTS
    // ========================================

    getUnifiedCampaignByCode: builder.query<any, string>({
      query: (code) => ({
        url: `/campaigns/unified/${code}`,
        method: "GET",
      }),
      providesTags: (_result, _error, code) => [{ type: Tags.CAMPAIGN, id: code }],
    }),

    // Histórico de auditoria de uma campanha. Bate em /audit-log/entity/:type/:id
    // que existe globalmente; entityType segue padrão CAMPAIGN_PRO0XX.
    getCampaignAuditHistory: builder.query<
      {
        entity_type: string;
        entity_id: string;
        total: number;
        events: Array<{
          id: string;
          eventType: string;
          createdAt: string;
          actorEmail: string | null;
          data: Record<string, any>;
        }>;
      },
      { type: "PRO013" | "PRO014" | "PRO017"; code: number }
    >({
      query: ({ type, code }) => ({
        url: `/audit-log/entity/CAMPAIGN_${type}/${code}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { type, code }) => [
        { type: Tags.CAMPAIGN, id: `audit-${type}-${code}` },
      ],
    }),

    getUnifiedCampaigns: builder.query<IUnifiedCampaignResponse, IUnifiedCampaignParams>({
      query: ({
        meta,
        campaignTypes,
        storeCode,
        storeCodes,
        status,
        startDateFrom,
        startDateTo,
        endDateFrom,
        endDateTo,
        search,
        sortBy,
        sortOrder,
        inEffect,
        itemCode,
        departmentCode,
        campaignCode,
      }) => {
        const perPage = meta.perPage ?? 20;
        const params = new URLSearchParams({
          page: meta.page.toString(),
          perPage: perPage.toString(),
        });

        if (campaignTypes && campaignTypes.length > 0) {
          campaignTypes.forEach(type => params.append("campaignTypes", type));
        }
        if (storeCodes && storeCodes.length > 0) {
          storeCodes.forEach(code => params.append("storeCodes", code.toString()));
        } else if (storeCode) {
          params.append("storeCode", storeCode.toString());
        }
        if (status && status.length > 0) {
          status.forEach(s => params.append("status", s));
        }
        if (startDateFrom) params.append("startDateFrom", startDateFrom);
        if (startDateTo) params.append("startDateTo", startDateTo);
        if (endDateFrom) params.append("endDateFrom", endDateFrom);
        if (endDateTo) params.append("endDateTo", endDateTo);
        if (search) params.append("search", search);
        if (sortBy) params.append("sortBy", sortBy);
        if (sortOrder) params.append("sortOrder", sortOrder);
        if (inEffect) params.append("inEffect", "true");
        if (itemCode) params.append("itemCode", itemCode);
        if (departmentCode) params.append("departmentCode", departmentCode);
        if (campaignCode) params.append("campaignCode", campaignCode);

        return {
          url: `/campaigns/unified?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.CAMPAIGNS],
    }),

    // ========================================
    // SKU/EXCEL UTILITIES
    // ========================================

    downloadSkuTemplate: builder.mutation<Blob, void>({
      query: () => ({
        url: `/campaigns/sku-template`,
        method: "GET",
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    parseSkuFile: builder.mutation<
      { skus: Array<{ sku: string; name?: string }>; count: number },
      File
    >({
      query: (file) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: `/campaigns/parse-sku-file`,
          method: "POST",
          body: formData,
        };
      },
    }),

    validateSkus: builder.mutation<
      { valid: Array<{ sku: string; name: string }>; invalid: string[] },
      { skus: string[] }
    >({
      query: (body) => ({
        url: `/campaigns/validate-skus`,
        method: "POST",
        body,
      }),
    }),

    // ========================================
    // ITEM SEARCH (ITE001 CATALOG)
    // ========================================

    searchItems: builder.query<
      { items: Array<{ code: string; name: string }> },
      { q: string; limit?: number }
    >({
      query: ({ q, limit = 20 }) => ({
        url: `/campaigns/items/search?q=${encodeURIComponent(q)}&limit=${limit}`,
        method: "GET",
      }),
      keepUnusedDataFor: 30,
    }),

    extendCampaignDate: builder.mutation<
      { message: string; data: { code: number; recordsUpdated: number; noop?: boolean } },
      { type: CampaignType; code: number; newEndDate: string }
    >({
      query: ({ type, code, newEndDate }) => ({
        url: `/campaigns/${type.toLowerCase()}/${code}/extend-date`,
        method: "PUT",
        body: { newEndDate },
      }),
      invalidatesTags: (_result, _error, { type, code }) => [
        Tags.CAMPAIGNS,
        { type: Tags.CAMPAIGN, id: `${type.toLowerCase()}-${code}` },
        { type: Tags.CAMPAIGN, id: `${type}-${code}` },
        { type: Tags.CAMPAIGN, id: `audit-${type}-${code}` },
      ],
    }),

    // Disparo manual da notificação de vigência (apenas homolog/dev — bloqueado em prod no back).
    triggerValidityNotification: builder.mutation<
      {
        campaignType: CampaignType;
        campaignCode: number;
        status: string;
        totalStores: number;
        successCount: number;
        failedCount: number;
      },
      { type: CampaignType; code: number }
    >({
      query: ({ type, code }) => ({
        url: `/campaigns/${code}/validity-notification/trigger`,
        method: "POST",
        body: { campaignType: type },
      }),
      invalidatesTags: (_result, _error, { type, code }) => [
        Tags.CAMPAIGNS,
        { type: Tags.CAMPAIGN, id: `${type}-${code}` },
      ],
    }),
  }),
});

export const {
  useGetCampaignsQuery,
  useGetCampaignByIdQuery,
  useCancelCampaignMutation,
  useExportCampaignProductsMutation,
  useCreateCampaignMutation,
  useCreatePro013CampaignMutation,
  useGetPro013CampaignByCodeQuery,
  useUpdatePro013CampaignMutation,
  useUpdatePro013CampaignStoresMutation,
  useCreatePro017CampaignMutation,
  useValidatePro017ConflictsMutation,
  useGetPro017CampaignByCodeQuery,
  useUpdatePro017CampaignMutation,
  useGetPro017ListItemsQuery,
  useAddPro017ListItemsMutation,
  useTogglePro017ListItemMutation,
  useGetUnifiedCampaignsQuery,
  useGetUnifiedCampaignByCodeQuery,
  useExtendCampaignDateMutation,
  useGetCampaignAuditHistoryQuery,
  useDownloadSkuTemplateMutation,
  useParseSkuFileMutation,
  useValidateSkusMutation,
  useSearchItemsQuery,
  useTriggerValidityNotificationMutation,
} = campaignService;
